import { useState } from "react";
import { Search, Sparkles, ArrowRight, ExternalLink, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const SUGGESTIONS = [
  "How much rent can I increase in Ontario in 2026?",
  "What deposit can I collect in BC?",
  "Notice periods to end a lease in Quebec",
  "Smart maintenance triage — what does it cost?",
];

/**
 * SmartModeSearch — homepage answer engine. Natural-language Q&A
 * grounded in our own content corpus with cited internal links.
 * Mounted at the top of Landing.jsx hero.
 */
export default function SmartModeSearch() {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = async (question) => {
    const text = (question || q || "").trim();
    if (!text) return;
    setQ(text);
    setBusy(true);
    setAnswer(null);
    try {
      const res = await fetch(`${API}/api/smart-mode/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: text }),
      });
      if (!res.ok) {
        // Never surface a raw error to the user. Prefer the server's
        // graceful fallback envelope when present, else render a soft
        // "warming up" notice that offers a related-topic link so the
        // visitor still ends the interaction with a next step.
        const j = await res.json().catch(() => ({}));
        if (j?.answer || j?.body || j?.text) {
          setAnswer({
            answer: j.answer || j.body || j.text,
            sources: j.sources || [],
            took_ms: j.took_ms || 0,
            fallback: true,
          });
          return;
        }
        setAnswer({
          answer: (
            "Here are two quick ways to get the answer you're looking for right now:\n\n" +
            "• Browse the Library at /library — 40+ landlord + tenant guides with the top questions already answered.\n" +
            "• Tap one of the suggested prompts above — those always work.\n\n" +
            "Live search will be back in a moment; try again shortly."
          ),
          sources: [
            { title: "Central Rentals Library", url: "/library" },
            { title: "Blog · comparisons + guides", url: "/blog" },
          ],
          took_ms: 0,
          fallback: true,
        });
        return;
      }
      const data = await res.json();
      setAnswer(data);
    } catch (e) {
      setAnswer({
        answer: "Please try again in a moment, or browse the Library at /library for answers to the top questions.",
        sources: [{ title: "Central Rentals Library", url: "/library" }],
        took_ms: 0,
        fallback: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="my-6 rounded-2xl border border-emerald-200 bg-white shadow-sm overflow-hidden" data-testid="smart-mode-search">
      <div className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 px-5 sm:px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
          <Sparkles className="w-3 h-3"/> Ask Mark Anything
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="smart-mode-input"
              placeholder="How much rent can I increase in Ontario? What's the deposit cap in BC?"
              maxLength={500}
              className="w-full pl-10 pr-4 py-3 rounded-full border border-slate-300 bg-white text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
          </div>
          <button type="submit" disabled={busy || !q.trim()} data-testid="smart-mode-ask"
            className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Sparkles className="w-4 h-4"/> Ask</>}
          </button>
        </form>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="smart-mode-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" onClick={() => ask(s)} disabled={busy}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:border-emerald-400 hover:text-emerald-700 text-slate-700">
              {s}
            </button>
          ))}
        </div>
      </div>

      {answer && (
        <div className="px-5 sm:px-6 py-5" data-testid="smart-mode-answer">
          {answer.error ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
              {answer.error}
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{answer.answer}</div>
              {(answer.sources || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Sources</div>
                  <div className="flex flex-wrap gap-1.5">
                    {answer.sources.map((s) => (
                      <a key={s.url} href={s.url} data-testid={`smart-mode-source-${s.url.replace(/[^a-z0-9]/gi,'-').slice(0,40)}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 font-semibold">
                        {s.title} <ExternalLink className="w-3 h-3"/>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 text-[10px] text-slate-400">
                Answered in {answer.took_ms}ms · Grounded in Central Rentals data — not the wider web
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
